import React, { useEffect, useState } from "react";
import { fetchInviteLinksByPool, updateInviteLinkPaidStatus, resendInviteEmail } from "./PoolModalHelper";
import { MdOutlineMail } from "react-icons/md";
import { IoCopyOutline } from "react-icons/io5";
import moment from "moment";
import { toast } from "react-toastify";
import "moment-timezone";
import { logError } from '../Utilities/errorLogger';
import { generateTeamUrl, copyToClipboard } from "../Utilities/shareUtils";

const ManagePoolEntries = ({ poolId, canEdit }) => {
  const [inviteLinks, setInviteLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    const loadInviteLinks = async () => {
      if (!initialLoad) return;
      try {
        const data = await fetchInviteLinksByPool(poolId);
        setInviteLinks(data);
      } catch (error) {
        await logError({ filePath: 'Pool/ManagePoolEntries.js', functionName: 'loadInviteLinks', errorType: 'Fetch Error', errorMessage: error.message, stackTrace: error.stack, requestData: { poolId } });
        toast.error("Failed to fetch entries", { theme: "dark" });
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    };
    loadInviteLinks();
  }, [poolId, initialLoad]);

  const handlePaidChange = async (id, paid) => {
    setInviteLinks(cl => cl.map(l => l.id === id ? { ...l, paid } : l));
    const result = await updateInviteLinkPaidStatus(id, paid);
    if (!result) {
      setInviteLinks(cl => cl.map(l => l.id === id ? { ...l, paid: !paid } : l));
      toast.error("Failed to update paid status", { theme: "dark" });
    }
  };

  const handleResendEmail = async (email, poolId) => {
    try {
      const response = await resendInviteEmail(email, poolId);
      if (response.ok) toast.success("Email resent!", { theme: "dark" });
      else throw new Error("Failed");
    } catch (error) {
      toast.error("Failed to resend email", { theme: "dark" });
    }
  };

  const handleCopyLink = async (magicToken) => {
    if (!magicToken) { toast.error("No team link available", { theme: "dark" }); return; }
    const success = await copyToClipboard(generateTeamUrl(magicToken));
    if (success) toast.success("Link copied!", { theme: "dark", autoClose: 2000 });
  };

  if (loading && initialLoad) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (inviteLinks.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <p className="text-gray-400">No entries yet. Invite players to get started.</p>
      </div>
    );
  }

  return (
    <div className="px-2 py-2">
      <div className="divide-y divide-gray-100">
        {inviteLinks.map((link) => {
          const displayName = link.team?.player_name || link.person?.full_name || link.email;
          const teamToken = link.team?.magic_token;

          return (
            <div key={link.id} className="flex items-center justify-between px-4 py-3">
              {/* Person Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
                {displayName !== link.email && (
                  <p className="text-xs text-gray-500 truncate">{link.email}</p>
                )}
                {link.invite_sent && (
                  <p className="text-xs text-gray-400 mt-0.5">Sent {moment(link.invite_sent).format("MMM D")}</p>
                )}
              </div>

              {/* Paid Toggle */}
              <div className="flex items-center gap-3 ml-4">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={link.paid}
                    onChange={(e) => handlePaidChange(link.id, e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                  <span className="ml-2 text-xs font-medium text-gray-500">{link.paid ? "Paid" : "Unpaid"}</span>
                </label>

                {/* Actions */}
                <button
                  onClick={() => handleCopyLink(teamToken)}
                  className="p-2 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-all"
                  title="Copy team link"
                >
                  <IoCopyOutline className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleResendEmail(link.email, link.pool?.id)}
                  disabled={!canEdit}
                  className="p-2 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-all disabled:opacity-30"
                  title="Resend email"
                >
                  <MdOutlineMail className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ManagePoolEntries;
